/**
 * CancellationModal - Modal for cancelling bookings with reason capture
 * Feature 009: Workshop Booking & Job-Tracking System
 * T088
 */

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui';
import { api } from '../../lib/api';

interface CancellationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onOpenChange: (open: boolean) => void;
  /** Booking ID to cancel */
  bookingId: string;
  /** Booking reference for display */
  bookingReference?: string;
  /** Customer name for display */
  customerName?: string;
  /** Scheduled date for display */
  scheduledAt?: Date | string | number;
  /** Callback on successful cancellation */
  onSuccess?: () => void;
  /** Query keys to invalidate on success */
  invalidateKeys?: string[][];
}

interface CancelledBooking {
  id: string;
  workflowStage: string;
}

const CANCELLATION_REASONS = [
  { value: 'customer_request', label: 'Solicitud del cliente' },
  { value: 'schedule_conflict', label: 'Conflicto de horario' },
  { value: 'vehicle_unavailable', label: 'Vehículo no disponible' },
  { value: 'parts_unavailable', label: 'Piezas no disponibles' },
  { value: 'staff_unavailable', label: 'Personal no disponible' },
  { value: 'duplicate_booking', label: 'Reserva duplicada' },
  { value: 'other', label: 'Otro motivo' },
];

export function CancellationModal({
  open,
  onOpenChange,
  bookingId,
  bookingReference,
  customerName,
  scheduledAt,
  onSuccess,
  invalidateKeys = [],
}: CancellationModalProps) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const cancelMutation = useMutation({
    mutationFn: async () => {
      // api.post returns data.data directly (the cancelled booking)
      // If the request fails, it throws an ApiError
      const booking = await api.post<CancelledBooking>(`/api/bookings/${bookingId}/cancel`, {
        reason,
        notes: notes.trim() || undefined,
      });
      return booking;
    },
    onSuccess: () => {
      // Invalidate related queries - use correct query keys
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', 'detail', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['bookings', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });

      onSuccess?.();
      handleClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleClose = useCallback(() => {
    setReason('');
    setNotes('');
    setError(null);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleConfirm = useCallback(() => {
    if (!reason) {
      setError('Por favor, selecciona un motivo de cancelación');
      return;
    }
    setError(null);
    cancelMutation.mutate();
  }, [reason, cancelMutation]);

  const formatDate = (date: Date | string | number) => {
    const d = new Date(date);
    return d.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Cancelar Cita
          </DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer. La cita será cancelada permanentemente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Booking info */}
          <div className="rounded-lg bg-gray-50 p-4 space-y-2">
            {bookingReference && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Referencia:</span>
                <span className="font-medium">{bookingReference}</span>
              </div>
            )}
            {customerName && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Cliente:</span>
                <span className="font-medium">{customerName}</span>
              </div>
            )}
            {scheduledAt && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Fecha:</span>
                <span className="font-medium capitalize">{formatDate(scheduledAt)}</span>
              </div>
            )}
          </div>

          {/* Reason selector */}
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Motivo de cancelación *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="cancel-reason">
                <SelectValue placeholder="Selecciona un motivo" />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Additional notes */}
          <div className="space-y-2">
            <Label htmlFor="cancel-notes">Notas adicionales (opcional)</Label>
            <textarea
              id="cancel-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Información adicional sobre la cancelación..."
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-gray-500 text-right">{notes.length}/500</p>
          </div>

          {/* Error message */}
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={cancelMutation.isPending}
          >
            Volver
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={cancelMutation.isPending || !reason}
          >
            {cancelMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cancelando...
              </>
            ) : (
              'Confirmar Cancelación'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
